/**
 * ADF (Atlassian Document Format) to TipTap JSON Converter
 * 
 * Converts between JIRA's ADF format and TipTap's ProseMirror-based JSON format.
 * Handles text formatting, mentions, images, code blocks, lists, and more.
 */

// =============================================================================
// ADF to TipTap Conversion
// =============================================================================

/**
 * Convert ADF document to TipTap JSON format
 * @param {Object} adf - Atlassian Document Format object
 * @returns {Object} TipTap-compatible JSON document
 */
export function adfToTiptap(adf) {
  if (!adf || adf.type !== 'doc') {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }]
    };
  }

  return {
    type: 'doc',
    content: (adf.content || []).map(convertAdfNodeToTiptap).filter(Boolean)
  };
}

/**
 * Convert a single ADF node to TipTap format
 */
function convertAdfNodeToTiptap(node) {
  if (!node) return null;

  switch (node.type) {
    case 'paragraph':
      return convertParagraph(node);
    case 'heading':
      return convertHeading(node);
    case 'bulletList':
      return convertBulletList(node);
    case 'orderedList':
      return convertOrderedList(node);
    case 'listItem':
      return convertListItem(node);
    case 'codeBlock':
      return convertCodeBlock(node);
    case 'blockquote':
      return convertBlockquote(node);
    case 'rule':
      return { type: 'horizontalRule' };
    case 'mediaSingle':
      return convertMediaSingle(node);
    case 'mediaGroup':
      return convertMediaGroup(node);
    case 'table':
      return convertTable(node);
    case 'panel':
      return convertPanel(node);
    case 'hardBreak':
      return { type: 'hardBreak' };
    default:
      // For unknown block types, try to extract text content
      if (node.content) {
        return {
          type: 'paragraph',
          content: node.content.map(convertAdfInlineToTiptap).filter(Boolean)
        };
      }
      return null;
  }
}

/**
 * Convert ADF inline nodes (text, mentions, etc.) to TipTap format
 */
function convertAdfInlineToTiptap(node) {
  if (!node) return null;

  switch (node.type) {
    case 'text':
      return convertText(node);
    case 'mention':
      return convertMention(node);
    case 'emoji':
      return convertEmoji(node);
    case 'inlineCard':
      return convertInlineCard(node);
    case 'hardBreak':
      return { type: 'hardBreak' };
    case 'media':
      return convertInlineMedia(node);
    default:
      // For unknown inline types, return as text
      if (node.text) {
        return { type: 'text', text: node.text };
      }
      return null;
  }
}

// Paragraph conversion
function convertParagraph(node) {
  return {
    type: 'paragraph',
    content: (node.content || []).map(convertAdfInlineToTiptap).filter(Boolean)
  };
}

// Heading conversion
function convertHeading(node) {
  return {
    type: 'heading',
    attrs: { level: node.attrs?.level || 1 },
    content: (node.content || []).map(convertAdfInlineToTiptap).filter(Boolean)
  };
}

// Bullet list conversion
function convertBulletList(node) {
  return {
    type: 'bulletList',
    content: (node.content || []).map(convertListItem).filter(Boolean)
  };
}

// Ordered list conversion
function convertOrderedList(node) {
  return {
    type: 'orderedList',
    attrs: { start: node.attrs?.order || 1 },
    content: (node.content || []).map(convertListItem).filter(Boolean)
  };
}

// List item conversion
function convertListItem(node) {
  return {
    type: 'listItem',
    content: (node.content || []).map(convertAdfNodeToTiptap).filter(Boolean)
  };
}

// Code block conversion
function convertCodeBlock(node) {
  const language = node.attrs?.language || '';
  const text = (node.content || [])
    .filter(n => n.type === 'text')
    .map(n => n.text)
    .join('');

  return {
    type: 'codeBlock',
    attrs: { language },
    content: text ? [{ type: 'text', text }] : []
  };
}

// Blockquote conversion
function convertBlockquote(node) {
  return {
    type: 'blockquote',
    content: (node.content || []).map(convertAdfNodeToTiptap).filter(Boolean)
  };
}

// Media (image) conversion from mediaSingle
function convertMediaSingle(node) {
  const media = node.content?.find(n => n.type === 'media');
  if (!media) return null;

  return {
    type: 'image',
    attrs: {
      src: media.attrs?.url || media.attrs?.id || '',
      alt: media.attrs?.alt || '',
      title: media.attrs?.title || '',
      // Store original JIRA attachment info for reference
      'data-attachment-id': media.attrs?.id || '',
      'data-attachment-type': media.attrs?.type || '',
      'data-collection': media.attrs?.collection || ''
    }
  };
}

// Media group conversion (multiple attachments)
function convertMediaGroup(node) {
  const images = (node.content || [])
    .filter(n => n.type === 'media')
    .map(media => ({
      type: 'image',
      attrs: {
        src: media.attrs?.url || media.attrs?.id || '',
        alt: media.attrs?.alt || '',
        title: media.attrs?.title || '',
        'data-attachment-id': media.attrs?.id || '',
        'data-attachment-type': media.attrs?.type || '',
        'data-collection': media.attrs?.collection || ''
      }
    }));

  // Return images wrapped in paragraphs
  if (images.length === 0) return null;
  if (images.length === 1) return images[0];
  
  return {
    type: 'paragraph',
    content: images
  };
}

// Inline media conversion
function convertInlineMedia(node) {
  return {
    type: 'image',
    attrs: {
      src: node.attrs?.url || node.attrs?.id || '',
      alt: node.attrs?.alt || '',
      title: node.attrs?.title || '',
      'data-attachment-id': node.attrs?.id || '',
      'data-attachment-type': node.attrs?.type || ''
    }
  };
}

// Table conversion
function convertTable(node) {
  return {
    type: 'table',
    content: (node.content || []).map(row => ({
      type: 'tableRow',
      content: (row.content || []).map(cell => ({
        type: cell.type === 'tableHeader' ? 'tableHeader' : 'tableCell',
        attrs: {
          colspan: cell.attrs?.colspan || 1,
          rowspan: cell.attrs?.rowspan || 1
        },
        content: (cell.content || []).map(convertAdfNodeToTiptap).filter(Boolean)
      }))
    }))
  };
}

// Panel conversion (info/warning/error boxes in JIRA)
function convertPanel(node) {
  const panelType = node.attrs?.panelType || 'info';
  return {
    type: 'blockquote',
    attrs: { 'data-panel-type': panelType },
    content: (node.content || []).map(convertAdfNodeToTiptap).filter(Boolean)
  };
}

// Text node conversion with marks
function convertText(node) {
  const result = {
    type: 'text',
    text: node.text || ''
  };

  if (node.marks && node.marks.length > 0) {
    result.marks = node.marks.map(mark => convertMark(mark)).filter(Boolean);
  }

  return result;
}

// Mark conversion (bold, italic, etc.)
function convertMark(mark) {
  switch (mark.type) {
    case 'strong':
      return { type: 'bold' };
    case 'em':
      return { type: 'italic' };
    case 'underline':
      return { type: 'underline' };
    case 'strike':
      return { type: 'strike' };
    case 'code':
      return { type: 'code' };
    case 'link':
      return {
        type: 'link',
        attrs: {
          href: mark.attrs?.href || '',
          target: mark.attrs?.target || '_blank',
          title: mark.attrs?.title || ''
        }
      };
    case 'textColor':
      return {
        type: 'textStyle',
        attrs: { color: mark.attrs?.color || '' }
      };
    case 'subsup':
      return mark.attrs?.type === 'sub' 
        ? { type: 'subscript' } 
        : { type: 'superscript' };
    default:
      return null;
  }
}

// Mention conversion
function convertMention(node) {
  return {
    type: 'mention',
    attrs: {
      id: node.attrs?.id || '',
      label: node.attrs?.text || node.attrs?.displayName || '',
      // Store original JIRA mention info
      'data-mention-type': node.attrs?.userType || 'user',
      'data-account-id': node.attrs?.id || ''
    }
  };
}

// Emoji conversion
function convertEmoji(node) {
  // TipTap doesn't have native emoji node, convert to text
  return {
    type: 'text',
    text: node.attrs?.text || node.attrs?.shortName || '😀'
  };
}

// Inline card (JIRA issue link) conversion
function convertInlineCard(node) {
  const url = node.attrs?.url || '';
  return {
    type: 'text',
    text: url,
    marks: [{
      type: 'link',
      attrs: { href: url, target: '_blank' }
    }]
  };
}

// =============================================================================
// TipTap to ADF Conversion
// =============================================================================

/**
 * Convert TipTap JSON document to ADF format
 * @param {Object} tiptap - TipTap JSON document
 * @returns {Object} Atlassian Document Format object
 */
export function tiptapToAdf(tiptap) {
  if (!tiptap || tiptap.type !== 'doc') {
    return {
      version: 1,
      type: 'doc',
      content: [{ type: 'paragraph', content: [] }]
    };
  }

  return {
    version: 1,
    type: 'doc',
    content: (tiptap.content || []).map(convertTiptapNodeToAdf).filter(Boolean)
  };
}

/**
 * Convert a single TipTap node to ADF format
 */
function convertTiptapNodeToAdf(node) {
  if (!node) return null;

  switch (node.type) {
    case 'paragraph':
      return convertTiptapParagraph(node);
    case 'heading':
      return convertTiptapHeading(node);
    case 'bulletList':
      return convertTiptapBulletList(node);
    case 'orderedList':
      return convertTiptapOrderedList(node);
    case 'listItem':
      return convertTiptapListItem(node);
    case 'codeBlock':
      return convertTiptapCodeBlock(node);
    case 'blockquote':
      return convertTiptapBlockquote(node);
    case 'horizontalRule':
      return { type: 'rule' };
    case 'image':
      return convertTiptapImage(node);
    case 'hardBreak':
      return { type: 'hardBreak' };
    default:
      // For unknown types, try to convert as paragraph
      if (node.content) {
        return {
          type: 'paragraph',
          content: node.content.map(convertTiptapInlineToAdf).filter(Boolean)
        };
      }
      return null;
  }
}

/**
 * Convert TipTap inline nodes to ADF format
 */
function convertTiptapInlineToAdf(node) {
  if (!node) return null;

  switch (node.type) {
    case 'text':
      return convertTiptapText(node);
    case 'mention':
      return convertTiptapMention(node);
    case 'hardBreak':
      return { type: 'hardBreak' };
    case 'image':
      // Inline images need special handling
      return null; // Will be handled at block level
    default:
      if (node.text) {
        return { type: 'text', text: node.text };
      }
      return null;
  }
}

// TipTap paragraph to ADF
function convertTiptapParagraph(node) {
  const content = (node.content || []).map(convertTiptapInlineToAdf).filter(Boolean);
  return {
    type: 'paragraph',
    content: content.length > 0 ? content : []
  };
}

// TipTap heading to ADF
function convertTiptapHeading(node) {
  return {
    type: 'heading',
    attrs: { level: node.attrs?.level || 1 },
    content: (node.content || []).map(convertTiptapInlineToAdf).filter(Boolean)
  };
}

// TipTap bullet list to ADF
function convertTiptapBulletList(node) {
  return {
    type: 'bulletList',
    content: (node.content || []).map(convertTiptapListItem).filter(Boolean)
  };
}

// TipTap ordered list to ADF
function convertTiptapOrderedList(node) {
  return {
    type: 'orderedList',
    attrs: { order: node.attrs?.start || 1 },
    content: (node.content || []).map(convertTiptapListItem).filter(Boolean)
  };
}

// TipTap list item to ADF
function convertTiptapListItem(node) {
  return {
    type: 'listItem',
    content: (node.content || []).map(convertTiptapNodeToAdf).filter(Boolean)
  };
}

// TipTap code block to ADF
function convertTiptapCodeBlock(node) {
  const language = node.attrs?.language || null;
  const text = (node.content || [])
    .filter(n => n.type === 'text')
    .map(n => n.text)
    .join('');

  return {
    type: 'codeBlock',
    attrs: language ? { language } : {},
    content: text ? [{ type: 'text', text }] : []
  };
}

// TipTap blockquote to ADF
function convertTiptapBlockquote(node) {
  // Check if it was originally a panel
  if (node.attrs?.['data-panel-type']) {
    return {
      type: 'panel',
      attrs: { panelType: node.attrs['data-panel-type'] },
      content: (node.content || []).map(convertTiptapNodeToAdf).filter(Boolean)
    };
  }

  return {
    type: 'blockquote',
    content: (node.content || []).map(convertTiptapNodeToAdf).filter(Boolean)
  };
}

// TipTap image to ADF mediaSingle
function convertTiptapImage(node) {
  const src = node.attrs?.src || '';
  const attachmentId = node.attrs?.['data-attachment-id'] || '';
  const collection = node.attrs?.['data-collection'] || '';

  // If we have attachment info, use it
  if (attachmentId) {
    return {
      type: 'mediaSingle',
      attrs: { layout: 'center' },
      content: [{
        type: 'media',
        attrs: {
          id: attachmentId,
          type: 'file',
          collection: collection || 'contentId'
        }
      }]
    };
  }

  // Otherwise, try to create from URL
  return {
    type: 'mediaSingle',
    attrs: { layout: 'center' },
    content: [{
      type: 'media',
      attrs: {
        type: 'external',
        url: src
      }
    }]
  };
}

// TipTap text to ADF
function convertTiptapText(node) {
  const result = {
    type: 'text',
    text: node.text || ''
  };

  if (node.marks && node.marks.length > 0) {
    result.marks = node.marks.map(convertTiptapMark).filter(Boolean);
  }

  return result;
}

// TipTap mark to ADF mark
function convertTiptapMark(mark) {
  switch (mark.type) {
    case 'bold':
      return { type: 'strong' };
    case 'italic':
      return { type: 'em' };
    case 'underline':
      return { type: 'underline' };
    case 'strike':
      return { type: 'strike' };
    case 'code':
      return { type: 'code' };
    case 'link':
      return {
        type: 'link',
        attrs: {
          href: mark.attrs?.href || ''
        }
      };
    case 'textStyle':
      if (mark.attrs?.color) {
        return {
          type: 'textColor',
          attrs: { color: mark.attrs.color }
        };
      }
      return null;
    case 'subscript':
      return { type: 'subsup', attrs: { type: 'sub' } };
    case 'superscript':
      return { type: 'subsup', attrs: { type: 'sup' } };
    default:
      return null;
  }
}

// TipTap mention to ADF mention
function convertTiptapMention(node) {
  return {
    type: 'mention',
    attrs: {
      id: node.attrs?.id || '',
      text: `@${node.attrs?.label || ''}`,
      userType: node.attrs?.['data-mention-type'] || 'DEFAULT'
    }
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Extract plain text from ADF document
 * @param {Object} adf - Atlassian Document Format object
 * @returns {string} Plain text content
 */
export function adfToPlainText(adf) {
  if (!adf || !adf.content) return '';

  function extractText(nodes) {
    return nodes.map(node => {
      if (node.type === 'text') {
        return node.text || '';
      }
      if (node.type === 'mention') {
        return `@${node.attrs?.text || node.attrs?.displayName || ''}`;
      }
      if (node.type === 'hardBreak') {
        return '\n';
      }
      if (node.content) {
        return extractText(node.content);
      }
      return '';
    }).join('');
  }

  return adf.content.map(block => {
    if (block.type === 'paragraph' || block.type === 'heading') {
      return extractText(block.content || []);
    }
    if (block.type === 'bulletList' || block.type === 'orderedList') {
      return (block.content || []).map(item => {
        return '• ' + extractText((item.content || [])[0]?.content || []);
      }).join('\n');
    }
    if (block.type === 'codeBlock') {
      return extractText(block.content || []);
    }
    if (block.content) {
      return extractText(block.content);
    }
    return '';
  }).join('\n\n');
}

/**
 * Check if ADF document is empty
 * @param {Object} adf - Atlassian Document Format object
 * @returns {boolean} True if document is empty
 */
export function isAdfEmpty(adf) {
  if (!adf || !adf.content || adf.content.length === 0) return true;
  
  const text = adfToPlainText(adf).trim();
  return text.length === 0;
}

/**
 * Create a simple ADF document from plain text
 * @param {string} text - Plain text content
 * @returns {Object} ADF document
 */
export function textToAdf(text) {
  const paragraphs = (text || '').split('\n\n').filter(p => p.trim());
  
  return {
    version: 1,
    type: 'doc',
    content: paragraphs.length > 0
      ? paragraphs.map(p => ({
          type: 'paragraph',
          content: [{ type: 'text', text: p }]
        }))
      : [{ type: 'paragraph', content: [] }]
  };
}

export default {
  adfToTiptap,
  tiptapToAdf,
  adfToPlainText,
  isAdfEmpty,
  textToAdf
};
